import { useState, useEffect } from 'react';
import type { FC } from 'react';
import type { Task, Driver, Zone, TaskType } from '../types';
import './NavigationView.css';

interface NavigationViewProps {
  task: Task;
  driver: Driver | null;
  zones: Zone[];
  onAccept: () => void;
  onStart: () => void;
  onArrivePickup: () => void;
  onLoad: () => void;
  onArriveDropoff: () => void;
  onComplete: () => void;
  onFail: (reason: string) => void;
  onClose: () => void;
}

const taskTypeLabels: Record<TaskType, string> = {
  pallet: '📦 Pall-flytt',
  return: '↩️ Retur',
  loading_dock: '🚚 Lastkaj',
  charging: '🔋 Laddning',
  internal_move: '🔄 Intern flytt',
  pick: '🛒 Plock',
  putaway: '📥 Inlagring',
};

const stepInstructions: Record<string, { icon: string; title: string; action: string }> = {
  navigate_pickup: { icon: '🚀', title: 'Navigera till upphämtning', action: 'Jag är framme' },
  at_pickup: { icon: '📍', title: 'Du är vid upphämtningen', action: 'Lastad & klar' },
  loading: { icon: '📦', title: 'Lastar...', action: 'Lastad & klar' },
  navigate_dropoff: { icon: '🚚', title: 'Navigera till leverans', action: 'Jag är framme' },
  at_dropoff: { icon: '📍', title: 'Du är vid leveransen', action: 'Levererat' },
  unloading: { icon: '📤', title: 'Lossar...', action: 'Levererat' },
  done: { icon: '✅', title: 'Uppdrag slutfört!', action: 'Stäng' },
};

const NavigationView: FC<NavigationViewProps> = ({
  task,
  driver: _driver,
  zones: _zones,
  onAccept,
  onStart,
  onArrivePickup,
  onLoad,
  onArriveDropoff,
  onComplete,
  onFail,
  onClose,
}) => {
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [problemReason, setProblemReason] = useState('');
  const [etaSeconds, setEtaSeconds] = useState<number | null>(null);

  // Countdown timer for ETA
  useEffect(() => {
    if (task.etaMinutes && task.status !== 'completed') {
      setEtaSeconds(task.etaMinutes * 60);
      
      const interval = setInterval(() => {
        setEtaSeconds(prev => {
          if (prev === null || prev <= 0) return 0;
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [task.etaMinutes, task.status]);

  const formatETA = (seconds: number | null) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentStep = stepInstructions[task.currentStep] || stepInstructions.navigate_pickup;
  
  const getCurrentLocation = () => {
    if (task.currentStep === 'navigate_pickup' || task.currentStep === 'at_pickup' || task.currentStep === 'loading') {
      return task.pickup;
    }
    return task.dropoff;
  };

  const getProgress = () => {
    const steps = ['navigate_pickup', 'at_pickup', 'loading', 'navigate_dropoff', 'at_dropoff', 'unloading', 'done'];
    const currentIndex = steps.indexOf(task.currentStep);
    return ((currentIndex + 1) / steps.length) * 100;
  };

  const handleAction = () => {
    switch (task.currentStep) {
      case 'navigate_pickup':
        onArrivePickup();
        break;
      case 'at_pickup':
      case 'loading':
        onLoad();
        break;
      case 'navigate_dropoff':
        onArriveDropoff();
        break;
      case 'at_dropoff':
      case 'unloading':
        onComplete();
        break;
      case 'done':
        onClose();
        break;
    }
  };

  const handleProblemSubmit = () => {
    if (problemReason.trim()) {
      onFail(problemReason);
      setShowProblemModal(false);
    }
  };

  const location = getCurrentLocation();
  const isGoingToPickup = task.currentStep === 'navigate_pickup' || task.currentStep === 'at_pickup' || task.currentStep === 'loading';

  return (
    <div className="navigation-view">
      {/* Top Bar - Task Info */}
      <div className="nav-top-bar">
        <button className="nav-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div className="nav-task-info">
          <span className="nav-task-type">{taskTypeLabels[task.type]}</span>
          <span className="nav-task-id font-mono">#{task.id.slice(-6)}</span>
        </div>
        {task.etaMinutes !== null && task.status !== 'completed' && (
          <div className="nav-eta">
            <span className="eta-label">ETA</span>
            <span className="eta-time font-mono">{formatETA(etaSeconds)}</span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="nav-progress">
        <div className="nav-progress-bar">
          <div className="nav-progress-fill" style={{ width: `${getProgress()}%` }} />
        </div>
        <div className="nav-progress-steps">
          <div className={`step ${task.currentStep === 'navigate_pickup' || task.currentStep === 'at_pickup' ? 'active' : getProgress() > 30 ? 'done' : ''}`}>
            <span className="step-icon">📍</span>
            <span className="step-label">Upphämtning</span>
          </div>
          <div className={`step ${task.currentStep === 'loading' ? 'active' : getProgress() > 45 ? 'done' : ''}`}>
            <span className="step-icon">📦</span>
            <span className="step-label">Lasta</span>
          </div>
          <div className={`step ${task.currentStep === 'navigate_dropoff' || task.currentStep === 'at_dropoff' ? 'active' : getProgress() > 75 ? 'done' : ''}`}>
            <span className="step-icon">🎯</span>
            <span className="step-label">Leverans</span>
          </div>
          <div className={`step ${task.currentStep === 'done' ? 'done' : ''}`}>
            <span className="step-icon">✅</span>
            <span className="step-label">Klar</span>
          </div>
        </div>
      </div>

      {/* Current Instruction */}
      <div className="nav-instruction">
        <div className="instruction-icon">{currentStep.icon}</div>
        <div className="instruction-content">
          <h2>{currentStep.title}</h2>
          <p className="instruction-location">
            <span className={`location-indicator ${isGoingToPickup ? 'pickup' : 'dropoff'}`}>
              {isGoingToPickup ? 'HÄMTA' : 'LEVERERA'}
            </span>
            {location.label}
          </p>
        </div>
      </div>

      {/* Location Card */}
      <div className="nav-location-card">
        <div className="location-card-header">
          <div className={`location-type ${isGoingToPickup ? 'pickup' : 'dropoff'}`}>
            {isGoingToPickup ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            )}
            {isGoingToPickup ? 'Upphämtningsplats' : 'Leveransplats'}
          </div>
          {task.distanceMeters && (
            <span className="location-distance font-mono">{Math.round(task.distanceMeters)}m</span>
          )}
        </div>
        
        <div className="location-details">
          <div className="location-main">
            <h3>{location.zoneName || location.zoneId}</h3>
            <p>
              {location.aisle && `Gång ${location.aisle}`}
              {location.aisle && location.shelf && ' · '}
              {location.shelf && `Hylla ${location.shelf}`}
            </p>
          </div>
          <div className="location-coords font-mono">
            {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
          </div>
        </div>

        {/* Route Preview - Simplified */}
        <div className="route-preview">
          <div className="route-line">
            <div className={`route-point start ${!isGoingToPickup ? 'done' : 'active'}`}>
              <div className="point-marker pickup" />
              <span>{task.pickup.zoneName || task.pickup.zoneId}</span>
            </div>
            <div className="route-connector">
              <svg width="100%" height="4" preserveAspectRatio="none">
                <line x1="0" y1="2" x2="100%" y2="2" stroke="var(--color-border)" strokeWidth="2" strokeDasharray="6,4"/>
              </svg>
            </div>
            <div className={`route-point end ${task.currentStep === 'done' ? 'done' : isGoingToPickup ? '' : 'active'}`}>
              <div className="point-marker dropoff" />
              <span>{task.dropoff.zoneName || task.dropoff.zoneId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Task Details */}
      {task.description && (
        <div className="nav-details">
          <h4>Uppdragsdetaljer</h4>
          <p>{task.description}</p>
          {task.weight && <span className="detail-badge">⚖️ {task.weight} kg</span>}
          {task.itemCount && <span className="detail-badge">📦 {task.itemCount} st</span>}
        </div>
      )}

      {/* Bottom Sheet - Actions */}
      <div className="nav-bottom-sheet">
        {task.status === 'assigned' ? (
          <div className="nav-accept-section">
            <div className="accept-info">
              <h3>Nytt uppdrag!</h3>
              <p>Du har tilldelats ett {taskTypeLabels[task.type].toLowerCase()}</p>
            </div>
            <div className="accept-actions">
              <button className="btn btn-decline" onClick={() => setShowProblemModal(true)}>
                Avböj
              </button>
              <button className="btn btn-accept" onClick={onAccept}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Acceptera
              </button>
            </div>
          </div>
        ) : task.status === 'accepted' ? (
          <div className="nav-start-section">
            <button className="btn btn-start" onClick={onStart}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              Starta Navigation
            </button>
          </div>
        ) : (
          <div className="nav-action-section">
            <button className="btn btn-problem" onClick={() => setShowProblemModal(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Problem
            </button>
            <button className="btn btn-action" onClick={handleAction}>
              {task.currentStep === 'done' ? (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Stäng
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  {currentStep.action}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Problem Modal */}
      {showProblemModal && (
        <div className="problem-modal-overlay">
          <div className="problem-modal">
            <div className="problem-modal-header">
              <h3>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Rapportera problem
              </h3>
              <button className="modal-close" onClick={() => setShowProblemModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            <div className="problem-options">
              {['Vara saknas', 'Fel plats', 'Truck problem', 'Blockerad passage', 'Annat'].map(reason => (
                <button 
                  key={reason}
                  className={`problem-option ${problemReason === reason ? 'selected' : ''}`}
                  onClick={() => setProblemReason(reason)}
                >
                  {reason}
                </button>
              ))}
            </div>
            
            <textarea
              placeholder="Beskriv problemet (valfritt)..."
              value={problemReason}
              onChange={(e) => setProblemReason(e.target.value)}
              rows={3}
            />
            
            <div className="problem-actions">
              <button className="btn btn-secondary" onClick={() => setShowProblemModal(false)}>
                Avbryt
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleProblemSubmit}
                disabled={!problemReason.trim()}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Avbryt uppdrag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationView;
